/**
 * Compute a diff between an existing state snapshot and an update payload.
 * Returns oldValue/newValue objects containing a `mutatedFields` array plus
 * the actual old and new values of those fields.
 *
 * Useful for audit logging across any module that needs to track what changed.
 *
 * @example
 * const diff = computeDiff(existingSnapshot, updatePayload);
 * // Returns: { oldValue: { mutatedFields: ['name', 'email'], name: '...', email: '...' }, ... }
 */
export function computeDiff(
  existingSnapshot: Record<string, unknown>,
  updatedBody: Record<string, unknown>,
): { oldValue: Record<string, unknown>; newValue: Record<string, unknown> } {
  const mutatedFields: string[] = [];
  const oldDiff: Record<string, unknown> = {};
  const newDiff: Record<string, unknown> = {};

  for (const key of Object.keys(updatedBody)) {
    const newVal = updatedBody[key];
    // Skip fields not actually being updated
    if (newVal === undefined) continue;

    const oldVal = existingSnapshot[key];

    // Only include fields that actually changed
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      mutatedFields.push(key);
      oldDiff[key] = oldVal;
      newDiff[key] = newVal;
    }
  }

  return {
    oldValue: { mutatedFields, ...oldDiff },
    newValue: { mutatedFields, ...newDiff },
  };
}
